/**
 * #688 Start Fresh Context: the owner's equivalent of branching to a fresh AI chat without
 * losing the task chain. Two canonical paths, no parallel implementation:
 *
 * - A Worker's persistent AI conversation: start_worker_fresh_context queues the closed node
 *   instruction; the node runs the same rollover/handoff lifecycle as automatic context-budget
 *   rotation (PTY and pins kept, durable handoff injected, acknowledgement required).
 * - Main Command / Overseer calls are stateless per call, so there is no vendor chat to reset:
 *   rotate_main_command_overseer_context (#450) rotates Brunel's own conversation context epoch.
 */

export interface FreshContextResult {
  readonly ok: boolean;
  readonly message: string;
}

type RpcResponse = PromiseLike<{ data: unknown; error: unknown }>;

/** Owner-facing sentence for each start_worker_fresh_context refusal. */
export function workerFreshContextErrorMessage(code: string): string {
  switch (code) {
    case 'worker_busy': return 'This Worker is working. Its AI context is never replaced mid-run; start a fresh context after the result is in.';
    case 'forbidden': return 'Confirm it is you (two-step verification) to start a fresh AI context.';
    case 'not_found': return 'This Worker has no current session to refresh.';
    case 'invalid_input': return 'That Worker session is not valid.';
    default: return 'A fresh context was not started.';
  }
}

/**
 * The Worker control. Success means the request reached the Worker's computer, never that the
 * context was replaced: the AI session line shows the new context and its lineage once the node
 * reports it, and the computer's answer (or refusal) is in its instruction log.
 */
export async function startWorkerFreshContext(input: {
  readonly orgId: string;
  readonly sessionId: string;
  readonly guard: (name: 'start_worker_fresh_context', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'start_worker_fresh_context', args: { readonly request: unknown }) => RpcResponse;
}): Promise<FreshContextResult> {
  let outcome: FreshContextResult = { ok: false, message: workerFreshContextErrorMessage('') };
  await input.guard('start_worker_fresh_context', async () => {
    const { data, error } = await input.rpc('start_worker_fresh_context', {
      request: { orgId: input.orgId, sessionId: input.sessionId },
    });
    const result = data as { ok?: boolean; error?: { code?: string } } | null;
    if (error || !result?.ok) {
      outcome = {
        ok: false,
        message: error && result === null
          ? 'Start Fresh Context is not available on this Brunel database yet.'
          : workerFreshContextErrorMessage(result?.error?.code ?? ''),
      };
      return;
    }
    outcome = {
      ok: true,
      message: 'Sent to the Worker\'s computer: it keeps the terminal and pins, hands the durable handoff to a fresh AI context, and reports the new context here.',
    };
  });
  return outcome;
}

/**
 * The Main Command control: a new Overseer context epoch from Brunel's own durable context,
 * not a vendor chat reset (each Overseer call is already stateless).
 */
export async function startMainCommandFreshContext(input: {
  readonly orgId: string;
  readonly rpc: (name: 'rotate_main_command_overseer_context', args: { readonly request: unknown }) => RpcResponse;
}): Promise<FreshContextResult> {
  const { data, error } = await input.rpc('rotate_main_command_overseer_context', {
    request: { orgId: input.orgId, reason: 'manual' },
  });
  const result = data as { ok?: boolean; error?: { code?: string }; value?: { overseerSessionEpoch?: number } } | null;
  if (error || !result?.ok) {
    return {
      ok: false,
      message: result?.error?.code === 'forbidden'
        ? 'Confirm it is you (two-step verification) to start a fresh Overseer context.'
        : 'A fresh Overseer context was not started.',
    };
  }
  const epoch = result.value?.overseerSessionEpoch;
  return {
    ok: true,
    message: `Fresh Overseer context${typeof epoch === 'number' ? ` #${epoch}` : ''}: the next reply starts from Brunel's durable context; this chat and its tasks are unchanged.`,
  };
}
